import React, { useMemo, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiMaximize2, FiMessageCircle, FiMinimize2, FiX } from "react-icons/fi";
import { CommentExplorer, CommentType } from "../../comments";
import { CommentAnchorType } from "../../../services/types";
import { getAnchorDisplayText } from "../utils/anchorText";
import { getAnchoredThreadComments } from "../utils/anchorThread";

interface AnchorThreadPanelProps {
  anchor: CommentAnchorType;
  comments: CommentType[];
  gistId: number;
  editId?: number | null;
  loading?: boolean;
  error?: unknown;
  currentUserId?: number;
  currentUserRole?: string;
  onClose: () => void;
  onReply?: (commentId: number, content: string) => Promise<void>;
  onSoftRemove?: (commentId: number) => Promise<void>;
  onHardDeleteThread?: (commentId: number) => Promise<void>;
}

function getAnchorAuthor(anchor: CommentAnchorType): string {
  return anchor.rootComment?.user?.name || anchor.createdBy?.name || "Anonymous";
}

function formatAnchorDate(anchor: CommentAnchorType): string {
  const rawDate = anchor.createdAt || anchor.rootComment?.createdAt;
  if (!rawDate) return "Unknown date";

  const parsed = Date.parse(rawDate);
  if (!Number.isFinite(parsed)) return "Unknown date";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

const AnchorThreadPanel: React.FC<AnchorThreadPanelProps> = ({
  anchor,
  comments,
  gistId,
  editId,
  loading = false,
  error,
  currentUserId,
  currentUserRole,
  onClose,
  onReply,
  onSoftRemove,
  onHardDeleteThread,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const threadComments = useMemo(
    () => getAnchoredThreadComments(anchor, comments || []),
    [anchor, comments]
  );

  const selectedText = getAnchorDisplayText(anchor);
  const isStale = anchor.status === "STALE";
  const isResolved = anchor.status === "RESOLVED";

  const statusIcon = isResolved
    ? React.createElement(FiCheckCircle, { "aria-hidden": "true" })
    : isStale
      ? React.createElement(FiAlertCircle, { "aria-hidden": "true" })
      : null;

  return React.createElement(
    "div",
    { className: `anchored-comment-thread-layer ${isExpanded ? "is-expanded" : "is-docked"}` },
    isExpanded
      ? React.createElement("button", {
          type: "button",
          className: "anchored-comment-thread-backdrop",
          onClick: () => setIsExpanded(false),
          "aria-label": "Return anchored discussion to side panel",
          title: "Return to side panel",
        })
      : null,
    React.createElement(
      "section",
      {
        className: `anchored-comment-thread-panel ${isExpanded ? "is-expanded" : "is-docked"}`,
        "aria-label": isExpanded ? "Expanded anchored discussion workspace" : "Focused anchored discussion",
      },
      React.createElement(
        "div",
        { className: "anchored-comment-thread-header" },
        React.createElement(
          "div",
          null,
          React.createElement(
            "div",
            { className: "anchored-comment-thread-kicker" },
            React.createElement(FiMessageCircle, { "aria-hidden": "true" }),
            React.createElement("span", null, isExpanded ? "Discussion Workspace" : "Discussion Focus")
          ),
          React.createElement("h3", null, isExpanded ? "Explore anchored discussion" : "Anchored discussion")
        ),
        React.createElement(
          "div",
          { className: "anchored-comment-thread-header-actions" },
          React.createElement(
            "button",
            {
              type: "button",
              className: "anchored-comment-thread-mode-toggle",
              onClick: () => setIsExpanded((current) => !current),
              "aria-label": isExpanded ? "Return anchored discussion to side panel" : "Expand anchored discussion workspace",
              title: isExpanded ? "Return to side panel" : "Explore discussion in a larger workspace",
            },
            React.createElement(isExpanded ? FiMinimize2 : FiMaximize2, { "aria-hidden": "true" }),
            React.createElement("span", null, isExpanded ? "Return to page" : "Explore")
          ),
          React.createElement(
            "button",
            {
              type: "button",
              className: "anchored-comment-thread-close",
              onClick: onClose,
              "aria-label": "Close anchored discussion panel",
              title: "Close discussion panel",
            },
            React.createElement(FiX, { "aria-hidden": "true" })
          )
        )
      ),
    React.createElement(
      "div",
      { className: "anchored-comment-thread-context" },
      React.createElement("div", { className: "anchored-comment-thread-context-label" }, "Selected passage"),
      React.createElement("blockquote", null, `“${selectedText}”`),
      React.createElement(
        "div",
        { className: "anchored-comment-thread-meta" },
        React.createElement("span", null, getAnchorAuthor(anchor)),
        React.createElement("span", null, "•"),
        React.createElement("span", null, formatAnchorDate(anchor)),
        React.createElement("span", null, "•"),
        React.createElement("span", null, `${threadComments.length} ${threadComments.length === 1 ? "comment" : "comments"}`),
        React.createElement("span", null, "•"),
        React.createElement(
          "span",
          { className: `anchored-comment-thread-status is-${anchor.status.toLowerCase()}` },
          statusIcon,
          anchor.status.toLowerCase()
        )
      )
    ),
    isStale
      ? React.createElement(
          "div",
          { className: "anchored-comment-thread-note is-warning" },
          "This discussion is attached to an older or changed text position. The thread is preserved, but the exact marker may need review."
        )
      : null,
    isResolved
      ? React.createElement(
          "div",
          { className: "anchored-comment-thread-note is-resolved" },
          "This anchored discussion has been marked resolved. Replies remain visible for context."
        )
      : null,
    React.createElement(CommentExplorer, {
      gistId,
      editId,
      comments: threadComments,
      loading,
      error,
      currentUserId,
      currentUserRole,
      title: "Focused discussion for this passage",
      subtitle: "Anchored thread",
      className: "anchored-comment-thread-explorer is-anchored-focus",
      replyUnavailableMessage: "Please login to reply to this anchored discussion.",
      showOrphanCount: false,
      onReply,
      onSoftRemove,
      onHardDeleteThread,
    })
  )
  );
};

export default AnchorThreadPanel;
